import { fetchJsonWithAuth, apiGetWithAuth } from './authApi';
import { AUTH_URL, SUBMISSIONS_URL } from './apiConfig';
import { normalizeDiceSkills, resolveDiceProfileId } from './diceCandidateMapper';

const TEST_FLOW_MODULE = 'test-flows';
const LMS_FLOW_MODULE = 'lms-flows';

const TEST_FLOW_FIELDS = [
  { value: 'module', label: 'Module', isVisible: true, type: 'text' },
  { value: 'flow', label: 'Flow', isVisible: true, type: 'text' },
  { value: 'step_order', label: 'Step Order', isVisible: true, type: 'number' },
  { value: 'keyword', label: 'Action', isVisible: true, type: 'text' },
  { value: 'description', label: 'Description', isVisible: true, type: 'text' },
  { value: 'target', label: 'Key', isVisible: true, type: 'text' },
  { value: 'tags', label: 'Tags', isVisible: true, type: 'text' },
  { value: 'value', label: 'Value', isVisible: true, type: 'text' },
  { value: 'expected', label: 'Expected', isVisible: true, type: 'text' },
];

const LMS_FLOW_FIELDS = [
  { value: 'module', label: 'Module', isVisible: true, type: 'text' },
  { value: 'flow', label: 'Flow', isVisible: true, type: 'text' },
  { value: 'step_order', label: 'Step Order', isVisible: true, type: 'number' },
  { value: 'keyword', label: 'Action', isVisible: true, type: 'text' },
  { value: 'description', label: 'Description', isVisible: true, type: 'text' },
  { value: 'target', label: 'Key', isVisible: true, type: 'text' },
  { value: 'tags', label: 'Tags', isVisible: true, type: 'text' },
  { value: 'value', label: 'Value', isVisible: true, type: 'text' },
  { value: 'expected', label: 'Expected', isVisible: true, type: 'text' },
];

function normalizeCandidateSearchSource(value) {
  const values = (Array.isArray(value) ? value : [value])
    .flat()
    .map((item) => {
      if (item && typeof item === 'object') {
        return item.value ?? item.label ?? item.name ?? '';
      }
      return item;
    })
    .map((item) => String(item ?? '').trim().toLowerCase())
    .filter(Boolean);

  if (values.includes('dice')) return 'dice';
  if (values.includes('internal')) return 'internal';
  return values[0] || '';
}

function firstFiniteNumber(...values) {
  for (const value of values) {
    const number = Number(value);
    if (Number.isFinite(number)) return number;
  }

  return undefined;
}

function extractResponseTotal(response, payload, rows, scope = '') {
  const responseCount = response?.count;
  const payloadCount = payload?.count;
  const normalizedScope = String(scope ?? '').trim().toLowerCase();

  if (responseCount && typeof responseCount === 'object') {
    const total = firstFiniteNumber(
      normalizedScope ? responseCount[normalizedScope] : undefined,
      responseCount.total,
      responseCount.searchCount,
      responseCount.count,
    );
    if (total !== undefined) return total;
  }

  if (payloadCount && typeof payloadCount === 'object') {
    const total = firstFiniteNumber(
      normalizedScope ? payloadCount[normalizedScope] : undefined,
      payloadCount.total,
      payloadCount.searchCount,
      payloadCount.count,
    );
    if (total !== undefined) return total;
  }

  return firstFiniteNumber(
    response?.total,
    typeof responseCount !== 'object' ? responseCount : undefined,
    response?.totalCount,
    payload?.total,
    typeof payloadCount !== 'object' ? payloadCount : undefined,
    payload?.totalCount,
    rows.length,
  ) ?? 0;
}

function extractResponseCounts(response, payload) {
  const counts = {};
  [response?.count, payload?.count].forEach((count) => {
    if (!count || typeof count !== 'object') return;

    Object.entries(count).forEach(([key, value]) => {
      const number = Number(value);
      if (Number.isFinite(number)) counts[String(key).trim().toLowerCase()] = number;
    });
  });

  return counts;
}

export async function getDropdownFields(module) {
  if (module === TEST_FLOW_MODULE) return TEST_FLOW_FIELDS;
  if (module === LMS_FLOW_MODULE) return LMS_FLOW_FIELDS;
  return apiGetWithAuth(SUBMISSIONS_URL, `/filter-dropdown-fields?module=${encodeURIComponent(module)}`);
}

export async function getFieldConfig(module) {
  if (module === TEST_FLOW_MODULE) return TEST_FLOW_FIELDS;
  if (module === LMS_FLOW_MODULE) return LMS_FLOW_FIELDS;
  return apiGetWithAuth(AUTH_URL, `/admin/field-config?module=${encodeURIComponent(module)}`);
}

export async function getDropdownValues(module, field, search = '', limit = 50, offset = 0, meta = {}) {
  const params = new URLSearchParams({
    module, field, value: search,
    limit: String(limit), offset: String(offset),
  });
  if (meta.dataSource) params.set('dataSource', meta.dataSource);
  if (meta.masterName) params.set('masterName', meta.masterName);
  if (meta.groupName) params.set('groupName', meta.groupName);
  // The Auth gateway owns the admin form configuration and understands
  // dataSource/masterName. Keeping this generic lets every configured master,
  // module and lookup field work without module-specific UI code.
  return apiGetWithAuth(AUTH_URL, `/filter-dropdown-values?${params}`);
}

export async function getModuleDataList(module, limit = 10, offset = 0, options = {}) {
  const { scope = '', sort = '', sortDir = '' } = options;

  if (module === TEST_FLOW_MODULE) {
    const { getTestFlows } = await import('./testingApi');
    const data = await getTestFlows({ limit, offset });
    const rows = Array.isArray(data?.items) ? data.items : [];
    const total = Number(data?.total) || 0;
    return { items: rows, total, limit, offset, tabs: [{ key: 'all', title: 'Test Cases', count: total }] };
  }

  if (module === LMS_FLOW_MODULE) {
    const { getLmsFlows } = await import('./testingApi');
    const data = await getLmsFlows({ limit, offset });
    const rows = Array.isArray(data?.items) ? data.items : [];
    const total = Number(data?.total) || 0;
    return { items: rows, total, limit, offset, tabs: [{ key: 'all', title: 'LMS Cases', count: total }] };
  }

  const params = new URLSearchParams({ module, limit: String(limit), offset: String(offset) });
  if (scope) params.set('scope', scope);
  if (sort) params.set('sort', sort);
  if (sortDir) params.set('sortDir', sortDir);

  return fetchJsonWithAuth(AUTH_URL, `/module-data-list?${params.toString()}`);
}

export async function searchModuleData(moduleName, searchParams = {}, pagination = {}, options = {}) {
  const { limit = 10, offset = 0 } = pagination;
  const { sort = '', sortDir = '', scope = '' } = options;
  const inferredScope = normalizeCandidateSearchSource(scope || searchParams.selectedSource);

  const query = new URLSearchParams();
  query.append('module', moduleName);
  query.append('limit', String(limit));
  query.append('offset', String(offset));

  if (inferredScope) {
    query.set('scope', inferredScope);
    console.log('[searchModuleData] Adding scope to query:', inferredScope);
  }
  if (sort) query.set('sort', sort);
  if (sortDir) query.set('sortDir', sortDir);

  console.log('[searchModuleData] Final query string:', query.toString());

  Object.entries(searchParams).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;

    let normalizedValue = value;
    if (typeof value === 'object') {
      normalizedValue = value.value ?? value.label ?? value.name ?? String(value);
    } else {
      normalizedValue = String(value);
    }

    query.append(key, normalizedValue);
  });

  const response = await fetchJsonWithAuth(AUTH_URL, `/module-data-list?${query.toString()}`);
  // const response = await fetchJsonWithAuth("http://localhost:9009/v1", `/module-data-list?${query.toString()}`);
  // response = { status, data: { actionRules, actions, columnActions, data: [...records] } }
  const payload = response?.data ?? response;
  const rows = Array.isArray(payload)
    ? payload
    : payload?.rows ?? payload?.records ?? payload?.items ?? payload?.list ?? payload?.data ?? [];

  const total = extractResponseTotal(response, payload, rows, inferredScope);
  const counts = extractResponseCounts(response, payload);
  if (inferredScope && counts[inferredScope] === undefined && Number.isFinite(total)) {
    counts[inferredScope] = total;
  }

  // Check if response contains Dice candidates and transform if needed
  const hasDiceCandidates = rows.some((record) => {
    const sourceFields = [
      record?.sourceType,
      record?.profileSource,
      record?.selectedSource,
      record?.customFields?.sourceType,
      record?.customFields?.profileSource,
    ].flat();
    return sourceFields.some(
      (value) => String(value ?? '').trim().toLowerCase() === 'dice',
    ) || Boolean(record?.customFields?.diceProfileData);
  });

  let normalizedRows = rows;
  if (hasDiceCandidates) {
    try {
      const { transformDiceResponseToInternalFormat } = await import('./diceCandidateMapper');
      const transformed = transformDiceResponseToInternalFormat(response, response);
      normalizedRows = transformed?.data?.data ?? rows;
    } catch (error) {
      console.error('[searchModuleData] Dice transformation failed:', error);
      normalizedRows = rows.map(normalizeCandidateSearchRow);
    }
  } else {
    normalizedRows = Array.isArray(rows) ? rows.map(normalizeCandidateSearchRow) : [];
  }

  return {
    rows: normalizedRows,
    total: Number(total) || 0,
    counts,
    tabs: response?.tabs ?? response?.tabList ?? payload?.tabs ?? payload?.tabList ?? [],
    fields: response?.fields ?? response?.fieldConfig ?? payload?.fields ?? payload?.fieldConfig ?? [],
    tabField: response?.tabField ?? payload?.tabField ?? '',
    actionRules: payload?.actionRules ?? [],
    actions: payload?.actions ?? [],
    columnActions: payload?.columnActions ?? [],
  };
}

function getNestedValue(record, path) {
  return String(path)
    .split('.')
    .reduce((value, key) => value?.[key], record);
}

function setNestedValue(record, path, value) {
  const keys = String(path).split('.').filter(Boolean);
  if (!keys.length) return record;

  const nextRecord = { ...record };
  let target = nextRecord;
  let source = record;

  keys.slice(0, -1).forEach((key) => {
    const currentValue = source?.[key];
    const nextValue = currentValue && typeof currentValue === 'object' && !Array.isArray(currentValue)
      ? { ...currentValue }
      : {};

    target[key] = nextValue;
    target = nextValue;
    source = currentValue;
  });

  target[keys[keys.length - 1]] = value;
  return nextRecord;
}

function normalizeCandidateSearchRow(row) {
  if (!row || typeof row !== 'object') return row;

  const skillPaths = [
    'skills',
    'technicalSkills',
    'primarySkills',
    'keySkills',
    'customFields.diceProfileData.skills',
  ];

  let nextRow = row;
  let firstNormalizedSkills = null;

  skillPaths.forEach((path) => {
    const rawSkills = getNestedValue(nextRow, path);
    if (rawSkills === undefined || rawSkills === null) return;

    const mappedSkills = normalizeDiceSkills(rawSkills);
    if (mappedSkills.length === 0 && Array.isArray(rawSkills) && rawSkills.length > 0) return;

    firstNormalizedSkills = firstNormalizedSkills ?? mappedSkills;
    nextRow = setNestedValue(nextRow, path, mappedSkills);
  });

  if (firstNormalizedSkills && getNestedValue(nextRow, 'skills') === undefined) {
    nextRow = setNestedValue(nextRow, 'skills', firstNormalizedSkills);
  }

  const sourceFields = [
    nextRow?.sourceType,
    nextRow?.profileSource,
    nextRow?.selectedSource,
    nextRow?.customFields?.sourceType,
    nextRow?.customFields?.profileSource,
  ].flat();
  const isDiceCandidate = sourceFields.some(
    (value) => String(value ?? '').trim().toLowerCase() === 'dice',
  ) || Boolean(nextRow?.customFields?.diceProfileData);

  if (isDiceCandidate) {
    const diceProfileId = resolveDiceProfileId(nextRow);
    const candidateId = nextRow?.candidateId
      ?? nextRow?.customFields?.diceProfileData?.candidateId
      ?? '';

    nextRow = {
      ...nextRow,
      id: nextRow?.id ?? diceProfileId,
      diceId: diceProfileId,
      candidateId,
    };
  }

  return nextRow;
}
